// ── ClientEngine.js ──────────────────────────────────────────────────────────
// Receives ENGINE_BOOTSTRAP once on connect, then ENGINE_TIME_UPDATE /
// ENGINE_PENALTY_UPDATE deltas. Computes rankings locally — no full JSON reload.
//
// Mirror of backend logic: backend/resultsEngine/{calculator,loader}.js
// Wire format defined in: backend/resultsEngine/engineProtocol.js

import { buildStageRanking, buildCumulativeRanking, buildTestStageRuns } from './engineCalculator';

export class ClientEngine {
  constructor() {
    this._data    = null;
    this._eventId = null;
    // Cache wyników per (widok) — czyszczony w całości przy każdej delcie,
    // bo sumy skumulowane przecinają odcinki. Daje też stabilną tożsamość
    // tablic między renderami, gdy dane się nie zmieniły.
    this._resultCache = new Map();
  }

  isLoaded()    { return this._data !== null; }
  getEventId()  { return this._eventId; }

  hasStage(stageId) {
    return !!this._data && this._data.stages.has(Number(stageId));
  }

  getStagePhase(stageId) {
    return this._data?.stages.get(Number(stageId))?.timingPhase ?? null;
  }

  // Załoga po numerze startowym (np. dla oznaczeń #6 w live komentarzach).
  getEntryByNumber(positionNumber) {
    if (!this._data) return null;
    const wanted = String(positionNumber);
    for (const entry of this._data.entries.values()) {
      if (String(entry.positionNumber) === wanted) return entry;
    }
    return null;
  }

  // ── Bootstrap ─────────────────────────────────────────────────────────────

  applyBootstrap(packet) {
    this._eventId = packet.eventId;

    const data = {
      competitions:  packet.competitions,
      stagesSorted:  packet.stagesSorted,
      stages:        new Map(packet.stages.map(s => [s.id, s])),
      entries:       new Map(packet.entries.map(e => [e.id, e])),
      classifications: new Map(packet.classifications.map(c => [c.id, c])),
      classEntries:  new Map(),
      times:         new Map(),
      penalties:     new Map(),
      testRuns:      new Map(),
    };

    for (const ce of packet.classEntries) {
      if (!data.classEntries.has(ce.classificationId))
        data.classEntries.set(ce.classificationId, new Map());
      data.classEntries.get(ce.classificationId).set(ce.entryListId, ce);
    }

    for (const t of packet.times) {
      if (!data.times.has(t.stageId)) data.times.set(t.stageId, new Map());
      const byEntry = data.times.get(t.stageId);
      if (!byEntry.has(t.entryListId)) byEntry.set(t.entryListId, new Map());
      byEntry.get(t.entryListId).set(t.classificationId, {
        timeSec: t.timeSec,
        taryfa:  t.taryfa === 1,
      });
    }

    for (const p of packet.penalties) {
      if (!data.penalties.has(p.entryListId)) data.penalties.set(p.entryListId, []);
      data.penalties.get(p.entryListId).push(p);
    }

    for (const r of packet.testRuns) {
      if (!data.testRuns.has(r.stageId)) data.testRuns.set(r.stageId, new Map());
      const byEntry = data.testRuns.get(r.stageId);
      if (!byEntry.has(r.entryListId)) byEntry.set(r.entryListId, []);
      byEntry.get(r.entryListId).push({ id: r.id, timeSec: r.timeSec });
    }

    this._data = data;
    this._resultCache.clear();
  }

  // ── Deltas ────────────────────────────────────────────────────────────────

  applyTimeUpdate(update) {
    if (!this._data) return;
    const { stageId, entryListId, classificationId, timeSec, taryfa } = update;
    if (!this._data.times.has(stageId)) this._data.times.set(stageId, new Map());
    const byEntry = this._data.times.get(stageId);
    if (!byEntry.has(entryListId)) byEntry.set(entryListId, new Map());
    byEntry.get(entryListId).set(classificationId, {
      timeSec: Number(timeSec),
      taryfa:  taryfa === 1,
    });
    this._resultCache.clear();
  }

  applyPenaltyUpdate(update) {
    if (!this._data) return;
    const { entryListId, stageId, competitionId, penaltyType, penaltySec } = update;
    if (!this._data.penalties.has(entryListId))
      this._data.penalties.set(entryListId, []);
    const list = this._data.penalties.get(entryListId);
    const idx  = list.findIndex(
      p => p.stageId === stageId && p.penaltyType === penaltyType && p.competitionId === competitionId
    );
    const entry = { stageId, competitionId, penaltyType, penaltySec: Number(penaltySec) };
    if (idx >= 0) list[idx] = entry;
    else list.push(entry);
    this._resultCache.clear();
  }

  // Zastępuje listę przebiegów TIME_ENTRY dla jednego zawodnika na odcinku testowym.
  applyTestRunUpdate(update) {
    if (!this._data) return;
    const { stageId, entryListId, runs } = update;
    if (!this._data.testRuns.has(stageId)) this._data.testRuns.set(stageId, new Map());
    this._data.testRuns.get(stageId).set(entryListId,
      runs.map(r => ({ id: r.id, timeSec: r.timeSec }))
    );
    this._resultCache.clear();
  }

  // Aktualizacja fazy odcinka (STAGE_PHASE_UPDATE) — nie wpływa na rankingi.
  applyStagePhase(stageId, timingPhase) {
    const stage = this._data?.stages.get(Number(stageId));
    if (stage) stage.timingPhase = timingPhase;
  }

  // ── Result queries ────────────────────────────────────────────────────────

  getStageResults(stageId, classificationId, groupId) {
    if (!this._data) return [];
    const stage = this._data.stages.get(stageId);
    if (!stage) return [];
    const cacheKey = `S|${stageId}|${classificationId}|${groupId || ''}`;
    if (this._resultCache.has(cacheKey)) return this._resultCache.get(cacheKey);
    const results = stage.isTest
      ? buildTestStageRuns(stageId, classificationId, this._data)
      : buildStageRanking(stageId, classificationId, groupId || null, this._data);
    this._resultCache.set(cacheKey, results);
    return results;
  }

  getAfterStageResults(stageId, classificationId, groupId) {
    if (!this._data) return [];
    const cacheKey = `A|${stageId}|${classificationId}|${groupId || ''}`;
    if (this._resultCache.has(cacheKey)) return this._resultCache.get(cacheKey);
    const prevStageId = this._prevClassifiedStageId(stageId);
    const prevRanking = prevStageId
      ? buildCumulativeRanking(prevStageId, classificationId, groupId || null, this._data, null)
      : null;
    const results = buildCumulativeRanking(stageId, classificationId, groupId || null, this._data, prevRanking);
    this._resultCache.set(cacheKey, results);
    return results;
  }

  // ── Private ───────────────────────────────────────────────────────────────

  _prevClassifiedStageId(stageId) {
    if (!this._data) return null;
    const target = this._data.stages.get(stageId);
    if (!target) return null;
    const compId = target.competitionId;

    let prevId    = null;
    let prevOrder = -1;

    for (const id of this._data.stagesSorted) {
      if (id === stageId) break;
      const s = this._data.stages.get(id);
      if (!s || s.competitionId !== compId) continue;
      if (!s.isClassified || s.canceled) continue;
      if (s.stageOrder > prevOrder) { prevOrder = s.stageOrder; prevId = id; }
    }

    return prevId;
  }
}
